import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, CardMedia,
  Grid, CircularProgress, Alert, Chip, IconButton,
  Avatar, Button
} from '@mui/material';
import { Facebook, Share, ThumbUp, Comment, Public } from '@mui/icons-material';
import api from '../services/api';

const FacebookFeed = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchFeed();
  }, []);

  const fetchFeed = async () => {
    try {
      const response = await api.get('/social/facebook?limit=20');
      setPosts(response.data.data || []);
    } catch (err) {
      setError('Facebook feed coming soon! Follow us on Facebook for updates.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress sx={{ color: '#1877f2' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 6, px: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Facebook sx={{ fontSize: 40, color: '#1877f2', mr: 2 }} />
        <Typography variant="h4" fontWeight={700} color="#1a237e">
          Facebook Feed
        </Typography>
        <Chip
          icon={<Public />}
          label="Live"
          color="success"
          size="small"
          sx={{ ml: 2 }}
        />
      </Box>

      {error || posts.length === 0 ? (
        <Card sx={{ borderRadius: '20px', p: 4, textAlign: 'center', bgcolor: '#fdfbf6' }}>
          <Facebook sx={{ fontSize: 60, color: '#1877f2', mb: 2 }} />
          <Typography variant="h6" color="#1a237e" gutterBottom>
            Facebook Feed Coming Soon
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Follow us on Facebook for the latest updates!
          </Typography>
          <Button
            variant="contained"
            href="https://facebook.com/natisguesthouse"
            target="_blank"
            startIcon={<Facebook />}
            sx={{
              bgcolor: '#1877f2',
              borderRadius: '50px',
              textTransform: 'none',
              px: 4,
              '&:hover': { bgcolor: '#0b5ed7' }
            }}
          >
            Follow on Facebook
          </Button>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {posts.map((post) => (
            <Grid item xs={12} md={6} key={post.id}>
              <Card sx={{ borderRadius: '20px', boxShadow: '0 8px 25px rgba(26,35,126,0.1)' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Avatar sx={{ bgcolor: '#1877f2', mr: 2 }}>
                      <Facebook />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700}>
                        Nati's Guesthouse
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(post.created_time)}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="body1" paragraph>
                    {post.message || 'No caption'}
                  </Typography>

                  {post.full_picture && (
                    <CardMedia
                      component="img"
                      height="auto"
                      image={post.full_picture}
                      alt="Post"
                      sx={{ borderRadius: 2, mb: 2 }}
                    />
                  )}

                  <Box display="flex" gap={3} mt={2}>
                    <Box display="flex" alignItems="center">
                      <ThumbUp fontSize="small" sx={{ mr: 0.5 }} />
                      <Typography variant="caption">
                        {post.likes?.summary?.total_count || 0}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center">
                      <Comment fontSize="small" sx={{ mr: 0.5 }} />
                      <Typography variant="caption">
                        {post.comments?.summary?.total_count || 0}
                      </Typography>
                    </Box>
                    <IconButton size="small">
                      <Share fontSize="small" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default FacebookFeed;